import { createAction as makeActionCreator } from 'redux-actions';
import moment from 'moment';

import api from '../api/';
import i18n, { BASE_NAMESPACES } from '../i18n';
import { fetchPathAfterLogin, loadRoot, saveStateAndSelectionHandler, verifyEmployerForSelectedPath } from './booking';
import { organizationReceived } from './organization';
import { checkUserFailure, identifyUserStart, identifyUserSuccess, bankAuthenticationStarted, bankAuthenticationLinkReceived } from './session';
import { handleError } from './errors';
import { fetchUponLogin } from './ehrResources';

export const fetchingLocalizations = makeActionCreator('FETCHING_LOCALIZATIONS');
export const loadingConfiguration = makeActionCreator('LOADING_CONFIGURATION');
export const configurationLoaded = makeActionCreator('CONFIGURATION_LOADED');
export const localizationsFetched = makeActionCreator('LOCALIZATIONS_FETCHED');
export const loginOnlyBySsoView = makeActionCreator('SHOW_LOGIN_ONLY_BY_SSO_VIEW');

let languages = [];
// Language of the organization default locale. The EHR reservation note is always
// written in it, so its bundles are needed even when the UI runs in another language.
let defaultLanguage = null;

let currentPathNamespace = null;
let currentEmployerId = null;

const loadedBundles = new Set();

// Accepts both i18next tags (en-US) and organization locales (fi_FI).
const baseLang = lang => (lang || 'fi').split(/[-_]/)[0];

const registerBundles = (localizationsByLanguage) => {
  localizationsByLanguage.forEach(({ language, namespaceMap }) => {
    namespaceMap.forEach(({ namespaceName, namespaceData }) => {
      i18n.addResourceBundle(language, namespaceName, namespaceData, true, true);
    });
  });
};

const fetchNamespacesForLanguages = (langs, namespaces) => (
  namespaces.length === 0 || langs.length === 0
    ? Promise.resolve()
    : api.requestAllByLanguages(langs, namespaces, currentEmployerId).then((res) => {
      registerBundles(res);
      langs.forEach(lang => namespaces.forEach(ns => loadedBundles.add(`${lang}:${ns}`)));
    })
);

const fetchNamespaces = (lang, namespaces) => fetchNamespacesForLanguages([lang], namespaces);

const loadMissingNamespaces = (lang, namespaces) => (
  fetchNamespaces(lang, namespaces.filter(ns => !loadedBundles.has(`${lang}:${ns}`)))
);

// Every namespace i18next knows about: the base ones, the ones the mounted components asked
// for (booking, forms, ...; i18next appends them to options.ns) and the current path namespace.
// The organization bucket may override any of them, e.g. booking.json carries the footer texts
// and time type names, so all of them have to be fetched from the organization.
const activeNamespaces = () => {
  const namespaces = [...new Set([...BASE_NAMESPACES, ...i18n.options.ns])];
  if (currentPathNamespace && !namespaces.includes(currentPathNamespace)) {
    namespaces.push(currentPathNamespace);
  }
  return namespaces;
};

// i18next skips the bundled /i18n/<lang>/<ns>.json file of a namespace that already has a
// resource bundle, so the bundled translations must be in place before any organization
// override is registered for the language. Loads the language (and its fallbacks) for every
// namespace i18next knows and also waits for the files a mounting component already requested.
// i18n.loadLanguages is not used because it returns at once for a language it has seen before.
// A missing bundled file (path namespaces only exist in the organization bucket) is expected
// and must not stop the load.
const loadBundledTranslations = lang => new Promise((resolve) => {
  const hierarchy = i18n.services.languageUtils.toResolveHierarchy(lang);
  i18n.services.backendConnector.load(hierarchy, i18n.options.ns, () => resolve());
});

// The bundled files of the component namespaces (booking, ...) are still being loaded when the
// organization fetch starts, and i18next would merge a late bundled file over the organization
// override. Wait for them first, then register the overrides on top.
const getLocalizations = lang => (
  loadBundledTranslations(lang).then(() => fetchNamespaces(lang, activeNamespaces()))
);

export const requestAuthentication = dispatch => (method, createUserIfNotExists) => {
  dispatch(bankAuthenticationStarted());
  api.requestAuthentication(method, createUserIfNotExists)
    .then((data) => {
      const { url } = data;
      dispatch(bankAuthenticationLinkReceived());
      dispatch(saveStateAndSelectionHandler)();
      // console.log(url);
      window.location.replace(url);
    })
    .catch((error) => {
      dispatch(bankAuthenticationLinkReceived());
      console.log(error);
    });
};

export const loadPathNamespace = (pathNamespace, employerId) => {
  if (!pathNamespace) {
    currentPathNamespace = null;
    currentEmployerId = null;
    return Promise.resolve();
  }

  currentPathNamespace = pathNamespace;
  currentEmployerId = employerId;

  // Always re-fetch the path namespace for the active language: the employer can change
  // between two loads of the same path (exclusive paths), and the bundle is registered with
  // overwrite, so the fetched texts replace the previous ones key by key. The previous bundle
  // is deliberately not removed first, which would leave the namespace empty until the
  // response arrives and make loadedBundles disagree with i18next in the meantime.
  const lang = baseLang(i18n.language);
  const activeLanguageLoad = fetchNamespaces(lang, [pathNamespace]);

  // The path titles of the EHR reservation note are always translated in the organization
  // default locale, and the UI switches to it after a strong authentication, so that
  // bundle has to be fetched as well. A failure there must not block the booking flow.
  let defaultLanguageLoad = Promise.resolve();
  if (defaultLanguage && defaultLanguage !== lang) {
    defaultLanguageLoad = fetchNamespacesForLanguages([defaultLanguage], [pathNamespace])
      .catch(error => console.error(`Could not load ${pathNamespace} in ${defaultLanguage}`, error));
  }

  return Promise.all([activeLanguageLoad, defaultLanguageLoad]).then(() => undefined);
};

// Single gateway for switching the UI language. Loads the active namespaces
// (base + current path) for the target language ONLY if they are not already
// loaded, then switches i18next + moment. Every language-change entry point
// must go through this so resources are guaranteed present before the switch.
export const changeLanguage = (newLang) => {
  const lang = baseLang(newLang);
  const ns = activeNamespaces();
  return loadBundledTranslations(lang)
    .then(() => loadMissingNamespaces(lang, ns))
    .then(() => new Promise(resolve => i18n.changeLanguage(lang, () => resolve())))
    .then(() => {
      moment.locale(lang);
    });
};


export const loadConfiguration = (dispatch, getState) => () => {
  const { booking, session } = getState();
  dispatch(loadingConfiguration());
  i18n.on('initialized', () => {
    dispatch(fetchingLocalizations());
    api.requestOrganization()
      .then((organizationData) => {
        dispatch(organizationReceived(organizationData));
        languages = organizationData.locales.map(locale => locale.split('_')[0]);
        defaultLanguage = organizationData.defaultLocale ? baseLang(organizationData.defaultLocale) : null;
        const detected = baseLang(i18n.language);
        const targetLang = languages.includes(detected) ? detected : 'fi';
        getLocalizations(targetLang)
          .then(() => {
            moment.locale(targetLang);
            return i18n.language !== targetLang ? i18n.changeLanguage(targetLang) : undefined;
          })
          .then(() => {
            // after all the localizations are loaded then we fetch the configurations
            dispatch(localizationsFetched());
            dispatch(identifyUserStart());
            api.checkUser()
              .then(data => dispatch(identifyUserSuccess(data)))
              .then(() => dispatch(loadRoot)())
              .then(() => dispatch(fetchUponLogin)())
              .then(() => dispatch(verifyEmployerForSelectedPath)())
              // in case user came after strong authentication we fetch originally selected path
              .then(() => dispatch(fetchPathAfterLogin)())
              // if user came after bank auth being in self paid path, load path localizations as pathconfig is not loaded second time
              .then(() => new Promise((resolve) => {
                if (booking.pathNamespace) {
                  loadPathNamespace(booking.pathNamespace, null)
                    .then(() => resolve());
                } else {
                  resolve();
                }
              }))
              .then(() => dispatch(configurationLoaded()))
              .catch(() => {
                dispatch(checkUserFailure('User is not logged in.'));
                dispatch(loadRoot)();
                dispatch(configurationLoaded());
              });
          });
      })
      .catch(() => {
        dispatch(configurationLoaded());
        return dispatch(handleError);
      });
  });
};

export const triggerLoginOnlyBySsoView = dispatch => () => dispatch(loginOnlyBySsoView());



// WEBPACK FOOTER //
// ./src/actions/app.js